/**
 * Become a listener from a talker account. Starting the flow must not flip
 * Talk / Listen / Both. A `listenerOnboardingStarted` flag tracks the attempt
 * until identity, payouts, and a published Listening profile are done.
 */

import { hasPublishedListeningProfile } from './ownListeningProfile';

export const LISTENER_ONBOARDING_STARTED_KEY = 'listenerOnboardingStarted';

const profileOf = currentUser => currentUser?.attributes?.profile || {};
const publicDataOf = currentUser => profileOf(currentUser).publicData || {};
const privateDataOf = currentUser => profileOf(currentUser).privateData || {};
const protectedDataOf = currentUser => profileOf(currentUser).protectedData || {};

const roleFrom = data => {
  const role = data?.accountRole;
  return role === 'talk' || role === 'listen' || role === 'both' ? role : null;
};

const inferredListenRole = currentUser => {
  const userType =
    publicDataOf(currentUser).userType ||
    privateDataOf(currentUser).userType ||
    protectedDataOf(currentUser).userType;
  const haystack = `${userType || ''}`.toLowerCase();
  // Only an explicit listen type. `provider` is Sharetribe’s default author
  // type and must not turn a talker-only account into a listener.
  return /listen/.test(haystack) ? 'listen' : null;
};

/**
 * Saved Talk / Listen / Both without importing accountRole (avoids a cycle).
 *
 * @param {Object} [currentUser]
 * @returns {'talk'|'listen'|'both'|null}
 */
export const accountRoleForOnboarding = currentUser =>
  roleFrom(publicDataOf(currentUser)) ||
  roleFrom(privateDataOf(currentUser)) ||
  roleFrom(protectedDataOf(currentUser)) ||
  inferredListenRole(currentUser);

/**
 * @param {Object} [currentUser]
 * @returns {boolean}
 */
export const hasListenerOnboardingStartedFlag = currentUser =>
  publicDataOf(currentUser)[LISTENER_ONBOARDING_STARTED_KEY] === true ||
  privateDataOf(currentUser)[LISTENER_ONBOARDING_STARTED_KEY] === true;

/**
 * Public + private flag so a dropped public key cannot lose the attempt.
 *
 * @param {boolean} [started]
 * @returns {{ publicData: Object, privateData: Object }}
 */
export const listenerOnboardingStartedData = (started = true) => ({
  publicData: { [LISTENER_ONBOARDING_STARTED_KEY]: started === true },
  privateData: { [LISTENER_ONBOARDING_STARTED_KEY]: started === true },
});

/**
 * Finished Become a listener. A published Listening profile is the gate —
 * the wizard already requires identity + payouts before publish.
 *
 * @param {Object} [params]
 * @returns {boolean}
 */
export const isListenerOnboardingComplete = ({
  currentUserHasListings = false,
  currentUserOwnListing = null,
  ownListings = [],
} = {}) =>
  hasPublishedListeningProfile({
    currentUserHasListings,
    currentUserOwnListing,
    ownListings,
  });

/**
 * Talker (or Both written too early) who started Become a listener and has
 * not finished. Listen-only signup is a listener by choice — not this path.
 *
 * @param {Object} [params]
 * @returns {boolean}
 */
export const isPendingBecomeListener = ({
  currentUser,
  currentUserHasListings = false,
  currentUserOwnListing = null,
  ownListings = [],
  currentUserHasListingsFetched,
} = {}) => {
  if (!currentUser?.id) {
    return false;
  }
  if (
    isListenerOnboardingComplete({
      currentUser,
      currentUserHasListings,
      currentUserOwnListing,
      ownListings,
    })
  ) {
    return false;
  }
  const role = accountRoleForOnboarding(currentUser);
  if (role === 'listen') {
    return false;
  }
  if (hasListenerOnboardingStartedFlag(currentUser)) {
    return true;
  }
  if (role !== 'both') {
    return false;
  }
  // Unfinished Both stays on Talking with finish/cancel. Do not flip
  // chrome while own listings are still loading — Dual-role skeleton
  // and a listings flag without a listing entity are not "no profile".
  if (currentUserHasListingsFetched === false) {
    return false;
  }
  if (
    currentUserHasListings &&
    !currentUserOwnListing &&
    !(Array.isArray(ownListings) && ownListings.length > 0)
  ) {
    return false;
  }
  return true;
};

/**
 * Talk-only hitting Become a listener / /l/new should persist the started
 * flag, not flip the account type.
 *
 * @param {Object} [currentUser]
 * @returns {boolean}
 */
export const shouldStartListenerOnboarding = (currentUser, listingParams = {}) => {
  if (!currentUser?.id) {
    return false;
  }
  if (hasListenerOnboardingStartedFlag(currentUser)) {
    return false;
  }
  const role = accountRoleForOnboarding(currentUser);
  if (role === 'listen') {
    return false;
  }
  if (isListenerOnboardingComplete({ currentUser, ...listingParams })) {
    return false;
  }
  return role === 'talk' || role === 'both' || !role;
};
